import './app.css'
import './tailwind.css'

import { IntlProvider } from 'react-intl'
import createCache from '@emotion/cache'
import type { EmotionCache } from '@emotion/react'
import { CacheProvider } from '@emotion/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { RouterProvider } from '@tanstack/react-router'

import { CssBaseline, ScopedCssBaseline, ThemeProvider } from '@gto/ui/material'
import { createGtoTheme } from '@gto/ui/theme'

import { isLocalEnv, isProdEnv } from '@/config/runtime-env'

import { defaultRichTextElements, useLocalization } from './localise'
import { queryClient } from './query-client'
import { createRouter } from './router'

const theme = createGtoTheme()

type AppProps = {
  cache?: EmotionCache
}

const createEmotionCache = () => {
  return createCache({
    key: 'rbwizard-css',
    speedy: isProdEnv(),
    prepend: true,
  })
}

export const App = ({ cache }: AppProps) => {
  const resolvedCache = cache ?? createEmotionCache()
  const { lang, messages } = useLocalization()

  return (
    <QueryClientProvider client={queryClient}>
      <CacheProvider value={resolvedCache}>
        <IntlProvider
          defaultRichTextElements={defaultRichTextElements}
          locale={lang}
          // the array syntax used for messages is not supported by the react-intl library
          // the array syntax is used e.g. in edge.plansSection.starterPlan.description
          messages={messages as unknown as Record<string, string>}
        >
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <ScopedCssBaseline
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                flex: '1 0 auto',
              }}
            >
              <RouterProviderWithContext />
            </ScopedCssBaseline>
          </ThemeProvider>
        </IntlProvider>
      </CacheProvider>
      {isLocalEnv() && <ReactQueryDevtools />}
    </QueryClientProvider>
  )
}

const RouterProviderWithContext = () => {
  return <RouterProvider router={createRouter()} />
}
